import { useEffect, useRef, useState } from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';

import SlotNumber from '../SlotNumber';
import CoinBurst from '../CoinBurst';

interface Props {
    points: number,
    children: React.ReactNode,
}

/**
 * Avatar badge that shows the user's monthly points. When the value increases
 * (a new report awarded points), the number reels and a coin burst erupts
 * from the avatar with arcing, falling-coin physics.
 */
export default function UserPointsBadge({ points, children }: Props) {
    const prevRef = useRef(points);
    const originRef = useRef<HTMLSpanElement | null>(null);
    const [burstKey, setBurstKey] = useState(0);

    useEffect(() => {
        if (points > prevRef.current) {
            setBurstKey(k => k + 1);
        }
        prevRef.current = points;
    }, [points]);

    const display = Math.min(points, 999);

    return (
        <>
            <Box ref={originRef} component="span" sx={{ display: 'inline-flex' }}>
                <Badge
                    badgeContent={
                        <SlotNumber value={display} format={(n) => (n >= 999 ? '999+' : String(n))} />
                    }
                    overlap="circular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{
                        '& .MuiBadge-badge': {
                            bgcolor: 'secondary.main',
                            color: 'secondary.light',
                        },
                    }}
                >
                    {children}
                </Badge>
            </Box>
            <CoinBurst triggerKey={burstKey} originRef={originRef} />
        </>
    );
}
